import { Navigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import BrandLoader from "@/components/BrandLoader";

/**
 * Redireciona rotas antigas `/internal/admin/<sub>` que na verdade eram de CONTA
 * para o caminho canônico `/internal/implementation/:clientId/<sub>`.
 *
 * O prefixo `admin/` misturava dois escopos: telas da conta selecionada
 * (billing, auditoria, métricas) e o CMS da plataforma (planos, conteúdo,
 * comunicados). Como a conta só existia no JWT, a URL não dizia em qual conta
 * você estava — recarregar, copiar o link ou abrir em outra aba perdia o
 * contexto visível.
 *
 * A conta ativa vem do `AuthContext`, que já reflete a troca do superadmin.
 * Enquanto ela não carregou não dá para montar o destino, então esperamos em
 * vez de redirecionar para um caminho com `undefined`.
 */
export default function LegacyAccountRouteRedirect({ to }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const accountId = user?.accountId;

  if (isLoading) return <BrandLoader inline />;

  // Sem conta resolvida não há destino possível; o dashboard decide o que fazer.
  if (!accountId) return <Navigate to="/internal/dashboard" replace />;

  return (
    <Navigate
      to={`/internal/implementation/${accountId}/${to}${location.search}`}
      replace
    />
  );
}

/**
 * Redireciona uma rota antiga de PLATAFORMA preservando o parâmetro `:id`.
 *
 * `<Navigate to="...">` recebe uma string literal e não interpola parâmetros de
 * rota, então casos como `admin/comunicados/:id/editar` precisam ler o param e
 * montar o destino.
 *
 * @param {{ build: (params: Record<string,string>) => string }} props
 */
export function LegacyPlatformRouteRedirect({ build }) {
  const params = useParams();
  const location = useLocation();
  return <Navigate to={`${build(params)}${location.search}`} replace />;
}
